import React, { useEffect, useState } from 'react';
import {
  Card, Input, Button, Space, Typography, Table, Tag, Descriptions,
  Form, Select, message, Empty, Spin, Alert,
} from 'antd';
import { EditOutlined, SaveOutlined } from '@ant-design/icons';
import { fiscalDataService } from '../../services/api';
import CaptureLinesPanel from './CaptureLinesPanel';
import EstimationPanel from './EstimationPanel';

const { Text } = Typography;

const REGIMENES = [
  { value: '601', label: '601 - General de Ley Personas Morales' },
  { value: '603', label: '603 - Personas Morales con Fines no Lucrativos' },
  { value: '605', label: '605 - Sueldos y Salarios' },
  { value: '612', label: '612 - Actividades Empresariales y Profesionales' },
  { value: '621', label: '621 - Incorporación Fiscal' },
  { value: '626', label: '626 - RESICO' },
];

/**
 * Gestión fiscal de una empresa: ficha, impuestos, líneas de captura y
 * estimación.
 *
 * Se monta dentro de la cartera al seleccionar un contribuyente, no como
 * sección aparte: el flujo natural es entrar por el cliente.
 */
const FichaFiscal = ({ companyId }) => {
  const [ficha, setFicha] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const cargar = async () => {
    setCargando(true);
    setError('');
    setEditando(false);

    try {
      const data = await fiscalDataService.getCompany(companyId);
      setFicha(data);
      form.setFieldsValue(data.empresa);
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo cargar la ficha fiscal');
      setFicha(null);
    } finally {
      setCargando(false);
    }
  };

  const guardar = async (values) => {
    setGuardando(true);
    try {
      await fiscalDataService.updateCompany(companyId, values);
      message.success('Datos fiscales actualizados');
      await cargar();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo actualizar');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <Card><Spin /> <Text type="secondary">Cargando ficha fiscal…</Text></Card>;
  }

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  if (!ficha) return null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        title={`Datos fiscales — ${ficha.empresa.tax_id}`}
        extra={
          editando ? (
            <Space>
              <Button onClick={() => { setEditando(false); form.setFieldsValue(ficha.empresa); }}>
                Cancelar
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={guardando} onClick={() => form.submit()}>
                Guardar
              </Button>
            </Space>
          ) : (
            <Button icon={<EditOutlined />} onClick={() => setEditando(true)}>Corregir</Button>
          )
        }
      >
        {editando ? (
          <Form form={form} layout="vertical" onFinish={guardar}>
            {/* El RFC no se edita: identifica al contribuyente y de él cuelgan
                la e.firma y los certificados. El backend además rechaza la
                petición completa si viene tax_id. */}
            <Form.Item label="RFC">
              <Input value={ficha.empresa.tax_id} disabled />
              <Text type="secondary" style={{ fontSize: 12 }}>
                El RFC no puede corregirse desde aquí.
              </Text>
            </Form.Item>
            <Form.Item name="tax_name" label="Razón social" rules={[{ required: true, message: 'Requerido' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="fiscal_regimen" label="Régimen fiscal">
              <Select options={REGIMENES} allowClear showSearch optionFilterProp="label" />
            </Form.Item>
            <Form.Item name="tax_location" label="Código postal">
              <Input maxLength={5} />
            </Form.Item>
            <Form.Item name="use_tax" label="Usa impuestos">
              <Select options={[{ value: 'Sí', label: 'Sí' }, { value: 'No', label: 'No' }]} />
            </Form.Item>
          </Form>
        ) : (
          <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
            <Descriptions.Item label="RFC">{ficha.empresa.tax_id}</Descriptions.Item>
            <Descriptions.Item label="Razón social">{ficha.empresa.tax_name}</Descriptions.Item>
            <Descriptions.Item label="Régimen">{ficha.empresa.fiscal_regimen || '—'}</Descriptions.Item>
            <Descriptions.Item label="Código postal">{ficha.empresa.tax_location || '—'}</Descriptions.Item>
            <Descriptions.Item label="Usa impuestos">{ficha.empresa.use_tax || '—'}</Descriptions.Item>
            <Descriptions.Item label="Actividades">
              {ficha.actividades?.length
                ? ficha.actividades.map((a) => <Tag key={a.code}>{a.code}</Tag>)
                : '—'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Card title="Impuestos configurados">
        <Table
          rowKey="id"
          dataSource={ficha.impuestos || []}
          pagination={false}
          size="small"
          scroll={{ x: true }}
          locale={{ emptyText: <Empty description="Sin impuestos configurados" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          columns={[
            { title: 'Código', dataIndex: 'tax_code', key: 'tax_code' },
            { title: 'Nombre', dataIndex: 'tax_name', key: 'tax_name' },
            { title: 'Tipo', dataIndex: 'tax_type', key: 'tax_type' },
            { title: 'Factor', dataIndex: 'tipo_factor', key: 'tipo_factor' },
            { title: 'Tasa', dataIndex: 'tasa_o_cuota', key: 'tasa_o_cuota' },
            {
              title: 'Estado',
              dataIndex: 'is_active',
              key: 'is_active',
              render: (a) => <Tag color={a ? 'green' : 'default'}>{a ? 'Activo' : 'Inactivo'}</Tag>,
            },
          ]}
        />
      </Card>

      <CaptureLinesPanel empresa={ficha.empresa} />

      <EstimationPanel empresa={ficha.empresa} />
    </Space>
  );
};

export default FichaFiscal;
